import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { PackagesGridWebComponent } from '../component/PackagesGridWebComponent';
import { PackagesGridView } from '../view/PackagesGridView';
import { templatePackages } from '../view/template';
import { Package } from '../types/packages-grid-package';
import { GridEvents } from '../../../grid/types/common';
import { PackageActionEnabler } from '../helpers/packageActionsEnabler';
import { DeleteDialog } from '../../uiElements/dialog/deleteDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import { PackagesRepository } from '../repository/PackagesRepository';
import { PackagesToastNotification } from '../../uiElements/notifications/toastNotification';
import { GridViewController } from '../../../grid/gridView/controller/GridViewController';
import { Column, ColumnKeys } from '../../../quarantine/computersAndVirtualMachines/types';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { PackageData } from '../types';
import { SendDownloadLinksDialog } from '../../uiElements/dialog/sendDownloadLinksDialog';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { OnAddPackage } from '../events/events';
import { MonthlyProductType } from '../../../license/types/MonthlyProductType';
import { translateFn } from '../../../utils/translator.helper';

export class PackagesGridController extends BaseWebComponentController {
  public component: PackagesGridWebComponent;
  public view: PackagesGridView;
  public viewConstructor: typeof PackagesGridView;
  public dataRepository: PackagesRepository;
  private selectedItems: Array<PackageData>;

  constructor(component: PackagesGridWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: PackagesGridView,
      viewTemplate: templatePackages,
      dataRepositoryConstructor: PackagesRepository,
    });
  }

  public async getDataForView(): Promise<{}> {
    return {};
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: ({ detail: { selectedItemsData } }) => this.onGridSelectedItems(selectedItemsData),
      },
      {
        element: this.view.dom.createPackage.$createPackageButton,
        event: ButtonEvents.CLICK,
        callback: this.emmitAddPackageEvent,
      },
      {
        element: this.view.dom.deletePackage.$deletePackageButton,
        event: ButtonEvents.CLICK,
        callback: this.openDeleteDialog,
      },
      {
        element: this.view.dom.sendDownloadLinks.$sendDownloadLinksButton,
        event: ButtonEvents.CLICK,
        callback: this.openSendDownloadDialog,
      },
    ];
  }

  private async emmitAddPackageEvent(): Promise<void> {
    this.view.dom.$grid.dispatchEvent(new OnAddPackage());
  }

  private attachDialogListeners(dialog: GzDialogWebComponent, confirmCallback: Function): void {
    dialog.addEventListener(GzDialogEvents.CONFIRM, event => confirmCallback(dialog, event));
    dialog.addEventListener(GzDialogEvents.CANCEL, () => dialog.close());
  }

  private openDeleteDialog(): void {
    const dialog = DeleteDialog.open(translateFn);

    this.attachDialogListeners(dialog, this.deleteEntries);
  }

  public async openSendDownloadDialog(): Promise<void> {
    try {
      const sendDownloadLinks = await this.dataRepository.getPackageLinks(this.selectedItems[0]._id);
      await SendDownloadLinksDialog.open(
        this.dependencies,
        this.view.dom.$hostElement,
        this.selectedItems[0],
        sendDownloadLinks
      );
    } catch (error: any) {
      if (error.err.exceptionType === 'DAL\\Exceptions\\SecurityException') {
        const errorMessage = this.dependencies.translateFn(
          'The selected installation package is not available. Please refresh the installation packages list.'
        );
        this.openFailureToast(errorMessage);
      }
    }
  }

  private deleteEntries = async (dialog: GzDialogWebComponent): Promise<void> => {
    const { selectedItems: records } = this.view.dom.$grid as { selectedItems: Array<Package> };
    dialog.close();

    if (records) {
      try {
        const requestStatus = await this.dataRepository.deleteSelected({ records });
        if (requestStatus.success) {
          this.openSuccessfulToast(this.dependencies.translateFn('Installation packages successfully deleted.'));
        } else {
          this.openFailureToast(this.dependencies.translateFn('Unable to delete installation packages'));
        }
      } catch (e) {
        this.openFailureToast(this.dependencies.translateFn('Unable to delete installation packages'));
      }
      await this.reloadGridItems();
    }
  };

  private async reloadGridItems(): Promise<void> {
    await (this.view.dom.$grid?.controller as unknown as GridViewController<Column, ColumnKeys>)?.handleGridItemsLoad();
  }

  private openSuccessfulToast(message: string): void {
    PackagesToastNotification.createToastNotification({
      message: message,
    });
  }

  private openFailureToast(message: string): void {
    PackagesToastNotification.createToastNotification({
      message: message,
      severity: ToastSeverity.ERROR,
    });
  }

  private onGridSelectedItems(selectedItems: Array<PackageData>): void {
    this.setDownloadButtonComponentProperties(selectedItems);
    this.selectedItems = selectedItems;
    PackageActionEnabler.handleActionButtons(this.view, selectedItems);
  }

  private setDownloadButtonComponentProperties(selectedItems): void {
    if (selectedItems.length !== 1) {
      return;
    }

    const item = selectedItems[0];
    this.view.dom.$downloadButton.setPackageDetails(item);
    this.view.dom.$downloadButton.setPackageType(item.type);
    this.view.dom.$downloadButton.setPackageId(item._id);
    this.view.dom.$downloadButton.setHasProxy(item.settings?.proxy?.enable);
    this.view.dom.$downloadButton.setPackageProductType(
      item.settings?.productType ?? MonthlyProductType.EndpointSecurity
    );
    this.view.dom.$downloadButton.setPackageCompanySettings(item.settings?.company);
    this.view.dom.$downloadButton.setOemKitId(item.oemKitId);
  }
}
